import type { ThemeConfig } from "antd";
import { theme } from "antd";
import { palettes, tint, type Palette } from "./tokens";

// ──────────────────────────────────────────────────────────────────────────
// NetScope Console — paired Dark / Light Ant Design themes
//
// Both are built by buildTheme() from the palettes in tokens.ts, so a colour
// is never written here. What differs between the two beyond the palette —
// which surface a card header sits on, how strong the primary glow is — is
// the small Variant object next to each export.
// ──────────────────────────────────────────────────────────────────────────

// Shared tokens that don't depend on background/text contrast.
const sharedTokens = {
  borderRadius:   10,
  borderRadiusLG: 14,
  borderRadiusSM: 6,
  borderRadiusXS: 4,

  fontFamily:
    'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontSize:         13,
  fontSizeHeading1: 32,
  fontSizeHeading2: 24,
  fontSizeHeading3: 19,
  fontSizeHeading4: 16,
  fontSizeHeading5: 14,
  lineHeight:       1.55,
  controlHeight:    34,
  controlHeightSM:  28,
};

const sharedComponents = {
  Button: {
    controlHeight:   34,
    controlHeightSM: 28,
    fontSize:        13,
    paddingInline:   16,
    fontWeight:      600,
  },
  Input: {
    controlHeight: 34,
    paddingInline: 12,
  },
  Select: {
    controlHeight: 34,
  },
  Form: {
    itemMarginBottom:     16,
    labelFontSize:        13,
    verticalLabelPadding: "0 0 4px",
  },
  Tabs: {
    titleFontSize:         13,
    horizontalItemPadding: "8px 18px",
  },
  Statistic: {
    titleFontSize:   12,
    contentFontSize: 28,
  },
  Badge: {
    statusSize: 7,
  },
};

// Variant is everything a theme decides that the palette alone does not.
interface Variant {
  algorithm:       ThemeConfig["algorithm"];
  elevatedBg:      string; // popovers, dropdowns
  borderSecondary: string;
  headerSurface:   string; // card and table headers
  modalHeaderBg:   string;
  rowHoverBg:      string;
  menuHoverColor:  string;
  primaryShadow:   string;
}

function buildTheme(p: Palette, v: Variant): ThemeConfig {
  const fills = p.statusFills;
  return {
    algorithm: v.algorithm,
    token: {
      ...sharedTokens,
      colorPrimary: p.cyan,
      colorInfo:    p.cyan,
      colorLink:    p.cyan,
      colorSuccess: p.green,
      colorWarning: p.amber,
      colorError:   p.red,

      ...(fills && {
        colorInfoBg:        fills.infoBg,
        colorInfoBorder:    fills.infoBorder,
        colorSuccessBg:     fills.successBg,
        colorSuccessBorder: fills.successBorder,
        colorWarningBg:     fills.warningBg,
        colorWarningBorder: fills.warningBorder,
        colorErrorBg:       fills.errorBg,
        colorErrorBorder:   fills.errorBorder,
      }),

      colorBorder:          p.line,
      colorBorderSecondary: v.borderSecondary,
      colorBgLayout:        p.bg0,
      colorBgContainer:     p.bg1,
      colorBgElevated:      v.elevatedBg,
      colorBgBase:          p.bg0,
      colorText:            p.text,
      colorTextSecondary:   p.muted,
      colorTextHeading:     p.strong,
      colorTextPlaceholder: p.faint,
    },
    components: {
      ...sharedComponents,
      Layout: {
        headerBg:      p.bg0,
        headerHeight:  52,
        headerPadding: "0 24px",
        siderBg:       p.bg0,
        bodyBg:        p.bg0,
        footerBg:      p.bg1,
        footerPadding: "10px 24px",
      },
      Menu: {
        itemBg:             "transparent",
        itemColor:          p.muted,
        itemSelectedBg:     tint(p.cyan, 0.1),
        itemSelectedColor:  p.cyan,
        itemHoverBg:        tint(p.hoverOverlay, 0.04),
        itemHoverColor:     v.menuHoverColor,
        itemHeight:         38,
        iconSize:           14,
        groupTitleColor:    p.faint,
        groupTitleFontSize: 12,
      },
      Card: {
        headerBg:       v.headerSurface,
        headerHeight:   44,
        headerFontSize: 14,
        paddingLG:      18,
        borderRadiusLG: 14,
      },
      Table: {
        headerBg:            v.headerSurface,
        headerColor:         p.muted,
        headerSplitColor:    p.line,
        borderColor:         p.line,
        rowHoverBg:          v.rowHoverBg,
        cellPaddingBlock:    9,
        cellPaddingInline:   12,
        cellPaddingBlockSM:  6,
        cellPaddingInlineSM: 8,
        cellFontSize:        13,
      },
      Button: {
        ...sharedComponents.Button,
        primaryShadow: v.primaryShadow,
      },
      Input: {
        ...sharedComponents.Input,
        activeBorderColor: p.cyan,
        hoverBorderColor:  p.cyan2,
      },
      Modal: {
        headerBg:       v.modalHeaderBg,
        titleFontSize:  15,
        titleColor:     p.strong,
        contentBg:      p.bg1,
        borderRadiusLG: 14,
      },
      Tabs: {
        ...sharedComponents.Tabs,
        inkBarColor:       p.cyan,
        itemSelectedColor: p.cyan,
        itemHoverColor:    p.cyan2,
      },
      Tag: {
        defaultBg:      tint(p.cyan, 0.1),
        defaultColor:   p.cyan,
        borderRadiusSM: 4,
      },
      Progress: {
        defaultColor: p.cyan,
      },
    },
  };
}

// ── Dark theme ────────────────────────────────────────────────────────────
// Deep navy: cards sit one step above the page, headers share the card
// surface, and elevated layers lift one step further.
const d = palettes.dark;
export const wansightTheme: ThemeConfig = buildTheme(d, {
  algorithm:       theme.darkAlgorithm,
  elevatedBg:      d.bg2,
  borderSecondary: d.line,
  headerSurface:   d.bg1,
  modalHeaderBg:   d.bg2,
  rowHoverBg:      d.bg2,
  menuHoverColor:  d.text,
  primaryShadow:   `0 6px 16px ${tint(d.cyan, 0.25)}`,
});

// ── Light theme ───────────────────────────────────────────────────────────
// Soft slate-blue for long shifts: headers drop back to the page colour so
// the off-white card body carries the content, and shadows stay faint.
const l = palettes.light;
export const wansightThemeLight: ThemeConfig = buildTheme(l, {
  algorithm:       theme.defaultAlgorithm,
  elevatedBg:      l.bg1,
  borderSecondary: l.bg3,
  headerSurface:   l.bg0,
  modalHeaderBg:   l.bg0,
  rowHoverBg:      l.bg3,
  menuHoverColor:  l.strong,
  primaryShadow:   `0 4px 12px ${tint(l.cyan, 0.18)}`,
});
